import type { TimelineSegment } from '../api/flashbackApi/types.ts';

export type { TimelineSegment };

function isPlaylistSegment(segment: TimelineSegment): boolean {
  return segment.status === 'complete' || segment.status === 'in-progress';
}

export function sortSegmentsChronological(segments: TimelineSegment[]): TimelineSegment[] {
  return [...segments].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );
}

export function getSegmentPlaybackOffsetSeconds(
  segments: TimelineSegment[],
  selectedSegmentId: string,
): number {
  const ordered = sortSegmentsChronological(segments);
  let offsetSeconds = 0;
  for (const segment of ordered) {
    if (segment.id === selectedSegmentId) return offsetSeconds;
    if (isPlaylistSegment(segment)) offsetSeconds += segment.durationSeconds;
  }
  return 0;
}

/**
 * Maps virtual playlist offset (seconds from start of concatenated segments) to wall-clock ms.
 */
export function wallClockMsFromPlaylistOffset(
  segments: TimelineSegment[],
  offsetSeconds: number,
): number | null {
  const ordered = sortSegmentsChronological(segments).filter(isPlaylistSegment);
  if (ordered.length === 0) return null;

  let remaining = offsetSeconds;
  if (remaining <= 0) {
    return new Date(ordered[0].startTime).getTime();
  }

  for (const seg of ordered) {
    const dur = seg.durationSeconds;
    if (remaining <= dur) {
      return new Date(seg.startTime).getTime() + remaining * 1000;
    }
    remaining -= dur;
  }

  return new Date(ordered[ordered.length - 1].endTime).getTime();
}

export interface PlaylistSeekResult {
  offsetSeconds: number;
  segmentId: string | null;
}

/**
 * Maps a wall-clock instant to virtual playlist time and the segment that should be active.
 * Gaps snap to the next segment's start in playlist space; time after the last segment snaps to its end.
 */
export function playlistSeekFromWallClockMs(
  segments: TimelineSegment[],
  wallMs: number,
): PlaylistSeekResult {
  const ordered = sortSegmentsChronological(segments).filter(isPlaylistSegment);
  if (ordered.length === 0) {
    return { offsetSeconds: 0, segmentId: null };
  }

  let playlistOffset = 0;
  for (const seg of ordered) {
    const start = new Date(seg.startTime).getTime();
    const end = new Date(seg.endTime).getTime();
    if (wallMs < start) {
      return { offsetSeconds: playlistOffset, segmentId: seg.id };
    }
    if (wallMs <= end) {
      return {
        offsetSeconds: playlistOffset + (wallMs - start) / 1000,
        segmentId: seg.id,
      };
    }
    playlistOffset += seg.durationSeconds;
  }

  const last = ordered[ordered.length - 1];
  return { offsetSeconds: playlistOffset, segmentId: last.id };
}

const END_EPSILON_S = 0.05;

/**
 * Resolves a virtual playlist offset (seconds) to a safe `HTMLMediaElement.currentTime`.
 * After `MANIFEST_PARSED`, HLS often reports a **partial** `video.duration` until the full
 * VOD span is known; clamping with that value would pin deep seeks to an early timestamp.
 */
export function resolveTimelineSeekTargetSeconds(
  offsetSeconds: number,
  mediaDurationSeconds: number | null,
): number {
  const safeOffset = Math.max(0, offsetSeconds);
  if (
    mediaDurationSeconds == null ||
    !Number.isFinite(mediaDurationSeconds) ||
    mediaDurationSeconds <= 0
  ) {
    return safeOffset;
  }
  const nearEnd = Math.max(0, mediaDurationSeconds - END_EPSILON_S);
  if (safeOffset <= nearEnd) {
    return Math.min(safeOffset, nearEnd);
  }
  return safeOffset;
}

/**
 * Maps scrubber seconds (elapsed within the selected segment) to `HTMLMediaElement.currentTime`.
 * Per-segment MP4s use local seconds; long concatenated timelines use virtual playlist offsets.
 */
export function resolveSegmentVideoSeekTargetSeconds(
  segments: TimelineSegment[],
  segment: TimelineSegment,
  progressSecondsWithinSegment: number,
  videoDurationSeconds: number | null,
): number {
  const clamped = Math.max(0, progressSecondsWithinSegment);
  if (shouldUseSegmentLocalVideoSeek(segment, videoDurationSeconds)) {
    return resolveTimelineSeekTargetSeconds(clamped, videoDurationSeconds);
  }
  const playlistOffset =
    getSegmentPlaybackOffsetSeconds(segments, segment.id) + clamped;
  return resolveTimelineSeekTargetSeconds(playlistOffset, videoDurationSeconds);
}

/** Sum of `durationSeconds` for segments that appear in the virtual playlist. */
export function playlistMetaTotalSeconds(segments: TimelineSegment[]): number {
  return sortSegmentsChronological(segments)
    .filter(isPlaylistSegment)
    .reduce((acc, s) => acc + s.durationSeconds, 0);
}

/** True when the media element timeline is a single-segment (or short) clip, not the full-day concat. */
export function isClipLengthPlaylist(
  segmentDurationSeconds: number,
  videoDurationSeconds: number | null,
): boolean {
  if (segmentDurationSeconds <= 0) return false;
  if (
    videoDurationSeconds == null ||
    !Number.isFinite(videoDurationSeconds) ||
    videoDurationSeconds <= 0
  ) {
    return false;
  }
  const slack = Math.max(2, segmentDurationSeconds * 0.25);
  return videoDurationSeconds <= segmentDurationSeconds + slack;
}

/**
 * True when scrubber seconds map 1:1 to `video.currentTime` (per-segment clip).
 * False for full-day concatenated playlists where seeks use virtual playlist offsets.
 */
export function shouldUseSegmentLocalVideoSeek(
  segment: TimelineSegment,
  videoDurationSeconds: number | null,
): boolean {
  const meta = Math.max(0.001, segment.durationSeconds);
  if (isClipLengthPlaylist(meta, videoDurationSeconds)) return true;
  // Scrubber is segment-local; until media duration is known, do not map to day playlist offset 0
  return videoDurationSeconds == null;
}

/**
 * Seconds elapsed within the selected segment from the main video element clock.
 */
export function segmentPlaybackSecondsFromVideo(
  segments: TimelineSegment[],
  segmentId: string,
  videoCurrentTime: number,
  videoDurationSeconds: number | null,
): number {
  const seg = segments.find((s) => s.id === segmentId);
  if (!seg) return 0;
  const segLen = Math.max(0.001, seg.durationSeconds);
  const t = Math.max(0, videoCurrentTime);
  const base = getSegmentPlaybackOffsetSeconds(segments, segmentId);

  const clipLike =
    isClipLengthPlaylist(segLen, videoDurationSeconds) ||
    (base > segLen * 0.5 && t <= segLen + 1.5);

  if (clipLike) {
    const uiDur = playbackUiDurationSeconds(seg, videoDurationSeconds);
    return Math.min(uiDur, t);
  }

  const mapped = offsetForWallClockMapping(
    segments,
    videoCurrentTime,
    videoDurationSeconds,
  );
  return Math.min(segLen, Math.max(0, mapped - base));
}

/** Length used for scrubber + end detection (min of API segment length and media duration). */
export function effectiveSegmentDurationSeconds(
  segment: TimelineSegment,
  videoDurationSeconds: number | null,
): number {
  const meta = Math.max(0.001, segment.durationSeconds);
  if (
    videoDurationSeconds != null &&
    Number.isFinite(videoDurationSeconds) &&
    videoDurationSeconds > 0
  ) {
    return Math.min(meta, videoDurationSeconds);
  }
  return meta;
}

/**
 * Scrubber span for a segment. Clip playlists use the longer of API vs media duration so
 * a 15s segment is not cut short when `video.duration` is slightly under-reported.
 */
export function playbackUiDurationSeconds(
  segment: TimelineSegment,
  videoDurationSeconds: number | null,
): number {
  const meta = Math.max(0.001, segment.durationSeconds);
  if (
    videoDurationSeconds != null &&
    Number.isFinite(videoDurationSeconds) &&
    videoDurationSeconds > 0 &&
    isClipLengthPlaylist(meta, videoDurationSeconds)
  ) {
    return Math.max(meta, videoDurationSeconds);
  }
  return effectiveSegmentDurationSeconds(segment, videoDurationSeconds);
}

/** True when playback has reached the end of the selected segment. */
export function isSegmentPlaybackAtEnd(
  segments: TimelineSegment[],
  segmentId: string,
  videoCurrentTime: number,
  videoDurationSeconds: number | null,
  videoEnded = false,
): boolean {
  if (videoEnded) return true;
  const seg = segments.find((s) => s.id === segmentId);
  if (!seg) return false;

  const meta = Math.max(0.001, seg.durationSeconds);
  const t = Math.max(0, videoCurrentTime);
  const clipLike = isClipLengthPlaylist(meta, videoDurationSeconds);

  if (clipLike) {
    if (
      videoDurationSeconds != null &&
      Number.isFinite(videoDurationSeconds) &&
      videoDurationSeconds > 0
    ) {
      return t >= videoDurationSeconds - END_EPSILON_S;
    }
    return t >= meta - END_EPSILON_S;
  }

  const rel = segmentPlaybackSecondsFromVideo(
    segments,
    segmentId,
    videoCurrentTime,
    videoDurationSeconds,
  );
  const effectiveDur = effectiveSegmentDurationSeconds(seg, videoDurationSeconds);
  return rel >= effectiveDur - 0.15;
}

/** Wall-clock ms for the playhead from segment-local progress seconds. */
export function wallClockMsForSegmentProgress(
  segment: TimelineSegment,
  progressSeconds: number,
): number {
  const startMs = new Date(segment.startTime).getTime();
  if (Number.isNaN(startMs)) return Date.now();
  return startMs + Math.max(0, progressSeconds) * 1000;
}

/**
 * Maps `video.currentTime` into the same virtual offset space as {@link wallClockMsFromPlaylistOffset}
 * when the media element's reported duration is **complete** (≥ meta span) but differs from API
 * metadata (e.g. EXTINF vs DB). Does **not** rescale when duration still looks partial (would map
 * `currentTime ≈ duration` to the wrong end of the day).
 */
export function offsetForWallClockMapping(
  segments: TimelineSegment[],
  videoCurrentTime: number,
  videoDurationSeconds: number | null,
): number {
  const t = Math.max(0, videoCurrentTime);
  const meta = playlistMetaTotalSeconds(segments);
  if (meta <= 0) return t;
  const dur =
    videoDurationSeconds != null &&
    Number.isFinite(videoDurationSeconds) &&
    videoDurationSeconds > 0
      ? videoDurationSeconds
      : null;
  if (dur == null) return t;

  const durationLooksComplete = dur >= meta * 0.98;
  const relDrift = Math.abs(meta - dur) / Math.max(meta, dur);
  if (!durationLooksComplete || relDrift < 0.02) return t;

  const scaled = (t / dur) * meta;
  return Math.min(Math.max(0, scaled), meta);
}
